/**
 * Sourcing workspace — the founder's home for brands they've explicitly
 * picked. Each card lets them kick off supplier discovery and read a
 * rolled-up pipeline snapshot. Click into a brand to drill down.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, Search } from 'lucide-react';
import type { BrandSizeBucket, SupplierPipelineStatus } from '@sourcito/schemas';
import { Badge, Button, Card, cn } from '../design';
import {
  useDiscoverSuppliersForBrand,
  useSourcingWorkspace,
  type SourcingWorkspaceBrand,
} from '../lib/sourcing';

const SIZE_LABELS: Record<BrandSizeBucket, string> = {
  micro: 'Micro',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

function sizeBadgeVariant(size: BrandSizeBucket | null): 'neutral' | 'accent' | 'positive' {
  if (size === 'micro' || size === 'small') return 'positive';
  if (size === 'medium') return 'accent';
  return 'neutral';
}

const PIPELINE_ORDER: SupplierPipelineStatus[] = [
  'lead',
  'contacted',
  'replied',
  'negotiating',
  'active',
  'passed',
];

const PIPELINE_LABELS: Record<SupplierPipelineStatus, string> = {
  lead: 'Lead',
  contacted: 'Contacted',
  replied: 'Replied',
  negotiating: 'Negotiating',
  active: 'Active',
  passed: 'Passed',
};

function pipelinePillVariant(status: SupplierPipelineStatus): 'neutral' | 'accent' | 'positive' {
  if (status === 'active') return 'positive';
  if (status === 'negotiating' || status === 'replied') return 'accent';
  return 'neutral';
}

export function SourcingPage() {
  const { data, isLoading, isError, error, refetch } = useSourcingWorkspace();

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center gap-3">
          <Link
            to="/"
            className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
          >
            <ChevronLeft className="size-5" />
          </Link>
          <div>
            <h1 className="text-xl font-medium leading-tight">Sourcing workspace</h1>
            <p className="text-xs text-fg-tertiary">
              Brands you&apos;ve marked Selected — research suppliers and run outreach
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8 space-y-4">
        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              {(error as Error)?.message ?? 'Failed to load workspace'}
            </p>
          </Card>
        )}
        {data && data.brands.length === 0 && (
          <Card className="text-center py-10">
            <h3 className="text-sm font-medium mb-1">No selected brands yet</h3>
            <p className="text-xs text-fg-tertiary max-w-md mx-auto">
              Pick a brand from{' '}
              <Link to="/brands" className="text-accent-fg hover:underline">
                /brands
              </Link>{' '}
              and mark it Selected. It will show up here as a sourcing workbench.
            </p>
          </Card>
        )}
        {data?.brands.map((b) => (
          <SourcingBrandCard key={`${b.domain}-${b.name}`} brand={b} onChanged={refetch} />
        ))}
      </main>
    </div>
  );
}

function SourcingBrandCard({
  brand,
  onChanged,
}: {
  brand: SourcingWorkspaceBrand;
  onChanged: () => void;
}) {
  const [kickoffMsg, setKickoffMsg] = useState<string | null>(null);
  const [kickoffError, setKickoffError] = useState<string | null>(null);
  const discover = useDiscoverSuppliersForBrand();

  async function onDiscover() {
    setKickoffMsg(null);
    setKickoffError(null);
    try {
      const res = await discover.mutateAsync(brand.name);
      if (res.runs.length === 0) {
        setKickoffMsg(
          'No ranked products in cache yet. Wait for an NL search to surface ASINs for this brand.'
        );
      } else {
        setKickoffMsg(
          `Discovery kicked off for ${res.runs.length} ASIN${res.runs.length === 1 ? '' : 's'} — candidates will appear in the review queue.`
        );
        onChanged();
      }
    } catch (e) {
      setKickoffError((e as Error).message ?? 'Discovery kickoff failed.');
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="space-y-3 flex-1 min-w-[300px]">
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/brands/$name"
              params={{ name: brand.name }}
              className="text-base font-medium hover:underline"
            >
              {brand.name}
            </Link>
            {brand.sizeBucket && (
              <Badge variant={sizeBadgeVariant(brand.sizeBucket)}>
                {SIZE_LABELS[brand.sizeBucket]}
              </Badge>
            )}
            <span className="text-xs text-fg-tertiary tabular-nums">
              {brand.productCount?.toLocaleString() ?? '—'} products
            </span>
            <span className="text-xs text-fg-tertiary tabular-nums">
              · {brand.supplierCount} supplier{brand.supplierCount === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="flex flex-wrap gap-2 text-xs">
            {PIPELINE_ORDER.map((status) => {
              const count = brand.pipelineStatusCounts[status];
              return (
                <li key={status}>
                  <Badge
                    variant={pipelinePillVariant(status)}
                    className={cn(count === 0 && 'opacity-40')}
                  >
                    {PIPELINE_LABELS[status]}{' '}
                    <span className="font-medium tabular-nums">{count}</span>
                  </Badge>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="space-y-2 w-full sm:w-[280px] text-right">
          <Button
            onClick={onDiscover}
            loading={discover.isPending}
            className="w-full sm:w-auto inline-flex items-center gap-2"
          >
            <Search className="size-4" />
            Discover suppliers
          </Button>
          {kickoffMsg && <p className="text-xs text-fg-secondary text-left">{kickoffMsg}</p>}
          {kickoffError && <p className="text-xs text-negative text-left">{kickoffError}</p>}
        </div>
      </div>
    </Card>
  );
}
